import { useCallback, useEffect } from 'react';

import { usePico } from '../pico';
import type { TrackAdditionalIdentifiers } from '../picox';

/**
 * Hook for tracking signup events in PicoX.
 */
export const useSignupTracking = () => {
  const pico = usePico();

  const trackSignupCompleted = useCallback(
    (marketingOptIn: boolean, additionalIdentifiers?: TrackAdditionalIdentifiers) => {
      if (pico === null) {
        return;
      }
      pico.track('signup_completed', { marketing_opt_in: marketingOptIn }, additionalIdentifiers);
    },
    [pico],
  );

  return { trackSignupCompleted };
};

const BACKEND_SIGNUP_COOKIE = 'backend_signup_completed';

/**
 * Side-effect hook that fires `signup_completed` for backend-initiated signups
 * (social OAuth, SSO). Because these flows involve a full-page redirect,
 * the backend sets a short-lived `backend_signup_completed` cookie after
 * creating the user. This hook reads it once on mount, fires the event, and
 * immediately expires the cookie so it is never sent twice.
 *
 * Cookie format: `<service>|<marketing_opt_in 0|1>` (e.g. "google|1", "sso|0").
 *
 * Must be called inside `PicoProvider`.
 */
export const useBackendSignupTracking = () => {
  const pico = usePico();

  useEffect(() => {
    if (pico === null) return;

    const match = document.cookie
      .split(';')
      .find((c) => c.trim().startsWith(`${BACKEND_SIGNUP_COOKIE}=`));

    if (!match) return;

    const cookieValue = match.trim().slice(BACKEND_SIGNUP_COOKIE.length + 1);
    const [registrationSource, marketingOptInRaw] = cookieValue.split('|');
    const marketingOptIn = marketingOptInRaw === '1';
    pico.track('signup_completed', {
      registration_source: registrationSource,
      marketing_opt_in: marketingOptIn,
    });

    // Expire the cookie immediately so it only fires once.
    // Use apex domain (e.g. "vimeo.com") so it matches SITE_COOKIE_DOMAIN regardless of subdomain (www, etc.).
    const apexDomain = window.location.hostname.split('.').slice(-2).join('.');
    document.cookie = `${BACKEND_SIGNUP_COOKIE}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${apexDomain}`;
  }, [pico]);
};
